/* eslint-disable */
import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  Calendar,
  MessageSquare,
  Settings,
  LogOut,
  Bell,
  Search,
  ChevronLeft,
  ChevronRight,
  Scale,
  Briefcase,
  BarChart3,
  Menu,
  X,
  BookOpen,
} from 'lucide-react';

const navItems = [
  { to: '/dashboard',           label: 'Overview',  icon: LayoutDashboard, end: true },
  { to: '/dashboard/blog',      label: 'Blog',      icon: BookOpen },
  { to: '/dashboard/settings',  label: 'Settings',  icon: Settings },
];

export default function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0a1628', fontFamily: "'Inter', sans-serif" }}>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 40,
            display: 'none',
          }}
          className="db-mobile-overlay"
        />
      )}

      {/* Sidebar */}
      <aside style={{
        width: collapsed ? 72 : 240,
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #0f1c3f 0%, #1a2d5a 60%, #1e3a6e 100%)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s cubic-bezier(.4,0,.2,1)',
        position: 'sticky',
        top: 0,
        flexShrink: 0,
        zIndex: 30,
        boxShadow: '4px 0 24px rgba(0,0,0,0.18)',
      }}>
        {/* Logo */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: collapsed ? '24px 16px' : '24px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          transition: 'padding 0.3s',
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: 'linear-gradient(135deg, #c9a84c, #f0c060)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 2px 12px rgba(201,168,76,0.35)',
          }}>
            <Scale size={20} color="#fff" />
          </div>
          {!collapsed && (
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, letterSpacing: 0.3 }}>LexAdmin</div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>Law Firm Panel</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '16px 8px', overflowY: 'auto' }}>
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: collapsed ? '11px 16px' : '11px 14px',
                borderRadius: 10,
                marginBottom: 4,
                textDecoration: 'none',
                color: isActive ? '#f0c060' : 'rgba(255,255,255,0.65)',
                background: isActive ? 'rgba(240,192,96,0.12)' : 'transparent',
                fontWeight: isActive ? 600 : 400,
                fontSize: 14,
                transition: 'all 0.18s',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                borderLeft: isActive ? '3px solid #f0c060' : '3px solid transparent',
              })}
            >
              <Icon size={18} style={{ flexShrink: 0 }} />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Bottom: back to site + collapse */}
        <div style={{ padding: '12px 8px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              width: '100%', padding: collapsed ? '10px 16px' : '10px 14px',
              borderRadius: 10, border: 'none', cursor: 'pointer',
              background: 'transparent',
              color: 'rgba(255,255,255,0.5)',
              fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden',
              transition: 'all 0.18s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#fff'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
          >
            <LogOut size={18} style={{ flexShrink: 0 }} />
            {!collapsed && <span>Back to Site</span>}
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(c => !c)}
          style={{
            position: 'absolute', top: 80,
            right: collapsed ? -12 : -12,
            width: 24, height: 24, borderRadius: '50%',
            background: '#1a2d5a',
            border: '2px solid rgba(255,255,255,0.15)',
            color: 'rgba(255,255,255,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10,
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f0c060'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#1a2d5a'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
        >
          {collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
        </button>
      </aside>

      {/* Main content area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Topbar */}
        <header style={{
          height: 64,
          background: '#0f1e38',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          display: 'flex', alignItems: 'center',
          padding: '0 28px',
          gap: 16,
          position: 'sticky', top: 0, zIndex: 20,
          boxShadow: '0 2px 20px rgba(0,0,0,0.25)',
        }}>
          {/* Search */}
          <div style={{
            flex: 1, maxWidth: 400,
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(255,255,255,0.06)', borderRadius: 10,
            padding: '0 14px', height: 40,
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            <Search size={15} color="rgba(255,255,255,0.35)" />
            <input
              placeholder="Search clients, cases, documents…"
              style={{
                border: 'none', background: 'transparent', outline: 'none',
                fontSize: 14, color: '#e2e8f0', width: '100%',
              }}
            />
          </div>

          <div style={{ flex: 1 }} />

          {/* Notifications */}
          <button style={{
            position: 'relative', width: 40, height: 40, borderRadius: 10,
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.18s',
          }}>
            <Bell size={18} color="rgba(255,255,255,0.6)" />
            <span style={{
              position: 'absolute', top: 8, right: 8,
              width: 8, height: 8, borderRadius: '50%',
              background: '#ef4444',
              border: '2px solid #0f1e38',
            }} />
          </button>

          {/* Avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'linear-gradient(135deg, #1a2d5a, #c9a84c)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 700, fontSize: 14,
            }}>A</div>
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>Admin</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Super Admin</div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, padding: '24px 24px', overflowY: 'auto', background: '#0a1628' }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
